import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import useReveal from "@/hooks/useReveal";
import { setPageSEO } from "@/lib/seo";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import WhatsAppFloat from "@/components/landing/WhatsAppFloat";
import SubpageHero from "@/components/landing/SubpageHero";
import BeforeAfter from "@/components/landing/BeforeAfter";
import StatsBanner from "@/components/landing/StatsBanner";
import FinalCTA from "@/components/landing/FinalCTA";
import { Rocket, Users, TrendingUp } from "lucide-react";

const BENEFITS = [
  {
    icon: Rocket,
    num: "01",
    title: "Implementación simple, sin burocracia",
    text: "EOS no requiere consultores externos permanentes ni procesos complejos. Es una metodología práctica que cualquier PYME puede implementar con el acompañamiento correcto y ver resultados en el primer trimestre.",
  },
  {
    icon: Users,
    num: "02",
    title: "Diseñado para empresas de 10 a 250 personas",
    text: "EOS fue creado específicamente para la etapa de crecimiento donde el dueño comienza a perder el control. No es un marco corporativo adaptado — es la herramienta exacta para el tamaño y momento de tu PYME.",
  },
  {
    icon: TrendingUp,
    num: "03",
    title: "Escala sin depender solo de ti",
    text: "Con EOS, tu equipo empieza a tomar decisiones alineadas sin que todo pase por el dueño. La empresa crece, y tú recuperas tiempo para trabajar en el negocio, no solo dentro de él.",
  },
];

export default function EOSParaPymes() {
  useReveal();

  useEffect(() => {
    setPageSEO({
      title: "EOS para PYMEs | Implementación EOS Pequeñas y Medianas Empresas Chile | Carlos Garcín",
      description: "EOS es la metodología ideal para PYMEs que quieren escalar con foco y disciplina. Carlos Garcín ayuda a pequeñas y medianas empresas a crecer con orden.",
      path: "/eos-para-pymes",
      keywords: "EOS para pymes, EOS pequeñas y medianas empresas, implementación EOS pyme Chile",
    });
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Header />
      <main>
        <SubpageHero
          eyebrow="EOS para Pequeñas y Medianas Empresas"
          title="Tu PYME puede escalar. Solo necesita la estructura correcta."
          subtitle="EOS fue diseñado exactamente para empresas como la tuya — en etapa de crecimiento, con potencial enorme, pero sin la estructura que permita escalar de forma ordenada y sostenida."
          withCta
        />

        {/* Por qué EOS encaja en PYMEs */}
        <section data-testid="pyme-benefits" className="section-padding bg-white">
          <div className="container-eos">
            <div className="max-w-3xl reveal">
              <span className="eyebrow">EOS y las PYMEs</span>
              <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy-800 leading-tight">
                Por qué EOS encaja perfectamente{" "}
                <span className="text-[#F26522]">en las PYMEs</span>
              </h2>
              <p className="mt-5 text-lg text-slate-600 max-w-2xl">
                No es un marco corporativo adaptado para empresas pequeñas. EOS nació para el momento exacto en que está tu empresa.
              </p>
            </div>
            <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {BENEFITS.map((b, idx) => (
                <article
                  key={idx}
                  data-testid={`pyme-benefit-${idx + 1}`}
                  className="reveal relative rounded-2xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:border-[#F26522]/40 hover:shadow-xl hover:-translate-y-1 overflow-hidden"
                  style={{ transitionDelay: `${idx * 90}ms` }}
                >
                  <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-[#F26522] to-[#F26522]/40" />
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-navy-800 flex items-center justify-center shadow-md">
                      <b.icon className="w-5 h-5 text-white" strokeWidth={1.7} />
                    </div>
                    <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#F26522]">
                      {b.num} / 03
                    </span>
                  </div>
                  <h3 className="mt-6 font-display font-extrabold text-2xl text-navy-800">{b.title}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{b.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <BeforeAfter />
        <StatsBanner />
        <FinalCTA />
      </main>
      <Footer />
      <WhatsAppFloat />
      <Toaster position="top-right" />
    </>
  );
}
